import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';

export interface CartItem {
  id: string;
  title: string;
  imageUrl: string;
  price: number;
  variant: string;
  quantity: number;
}

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly storageKey = 'wembely_cart';
  private readonly itemsSubject = new BehaviorSubject<CartItem[]>(
    this.readStoredItems(),
  );
  private readonly discountSubject = new BehaviorSubject<number>(0);

  readonly items$ = this.itemsSubject.asObservable();
  readonly discount$ = this.discountSubject.asObservable();
  readonly total$ = this.items$.pipe(
    map((items) =>
      items.reduce((acc, item) => acc + item.price * item.quantity, 0),
    ),
  );

  incrementQuantity(id: string): void {
    this.updateItems((item) =>
      item.id === id ? { ...item, quantity: item.quantity + 1 } : item,
    );
  }

  decrementQuantity(id: string): void {
    this.updateItems((item) =>
      item.id === id
        ? { ...item, quantity: Math.max(1, item.quantity - 1) }
        : item,
    );
  }

  removeItem(id: string): void {
    this.itemsSubject.next(
      this.itemsSubject.value.filter((item) => item.id !== id),
    );
    this.persist();
  }

  addItem(item: CartItem): void {
    const existing = this.itemsSubject.value.find(
      (cartItem) =>
        cartItem.id === item.id && cartItem.variant === item.variant,
    );

    if (existing) {
      this.itemsSubject.next(
        this.itemsSubject.value.map((cartItem) =>
          cartItem.id === item.id && cartItem.variant === item.variant
            ? { ...cartItem, quantity: cartItem.quantity + item.quantity }
            : cartItem,
        ),
      );
      this.persist();
      return;
    }

    this.itemsSubject.next([...this.itemsSubject.value, item]);
    this.persist();
  }

  private updateItems(updater: (item: CartItem) => CartItem): void {
    this.itemsSubject.next(this.itemsSubject.value.map(updater));
    this.persist();
  }

  private persist(): void {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(
      this.storageKey,
      JSON.stringify(this.itemsSubject.value),
    );
  }

  private readStoredItems(): CartItem[] {
    if (typeof window === 'undefined') {
      return [];
    }
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  clearCart(): void {
    this.itemsSubject.next([]);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.storageKey);
    }
  }
}
